import { ListItemButton, ListItemText, Typography } from "@mui/material";
import { Search, useDebounce, useSearch } from "@ssf/components";
import { searchMembers } from "@ssf/memdb-api";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export const TeamLeaderSearch = ({
  onSelect,
}: {
  onSelect: (memberId: number) => void;
}) => {
  const { t } = useTranslation("TeamLeaderSearch");
  const searchProps = useSearch();
  const { setOpen } = searchProps;
  const searchTerm = searchProps.value.trim();
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const canSearch =
    searchTerm.length >= 2 && searchTerm === debouncedSearchTerm;
  const { data, isFetching, isError } = useQuery({
    queryKey: ["memdbSearchV3", "member", debouncedSearchTerm, 10],
    queryFn: () => searchMembers({ q: debouncedSearchTerm, limit: 10 }),
    enabled: searchProps.open && canSearch,
  });

  useEffect(() => setOpen(true), [setOpen]);

  return (
    <Search
      {...searchProps}
      options={canSearch ? (data ?? []) : []}
      getKey={({ option }) => String(option.id)}
      placeholder={t("placeholder")}
      onSelect={({ option }) => onSelect(option.id)}
      renderItem={({ option }) => (
        <ListItemButton onClick={() => onSelect(option.id)}>
          <ListItemText
            primary={[option.firstName, option.lastName]
              .filter(Boolean)
              .join(" ")}
            secondary={[option.club?.name, option.city, String(option.id)]
              .filter(Boolean)
              .join(" · ")}
          />
        </ListItemButton>
      )}
      noOptions={
        searchProps.open && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {searchTerm.length < 2
              ? t("minCharacters")
              : !canSearch || isFetching
                ? t("loading")
                : isError
                  ? t("error")
                  : t("noResults")}
          </Typography>
        )
      }
    />
  );
};
